import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowRight, ListFilter, Plus, Search, X } from 'lucide-react'
import {
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  ROUTE_ORDERS,
  ROUTE_RECEIVABLE
} from '@klear/constants'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { RECEIVABLE_TABS, type ReceivableRowTab } from '@klear/invoice-offer/receivables'
import type { ReceivableRow } from '@klear/order-event/receivable'
import { type OrderEventActionFormValues } from '@klear/order-event/event-form'
import { EMPTY_ORDER_EVENT_ACTION_FORM } from '@klear/order-event/wire.client'
import { EventActionsConfirmModal } from '@klear/order-event-ui/event-actions-confirm-modal'
import { Badge } from '@klear/ui/components/badge'
import { ButtonLink } from '@klear/ui/components/button-link'
import { Input } from '@klear/ui/components/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@klear/ui/components/tabs'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { useEventActionConfig } from '@klear/ui/hooks/use-event-action-config'
import { orderEventRefFromOffer } from '@klear/ui/hooks/order-event-ref'
import { useOrderEventDocuments } from '@klear/ui/hooks/use-order-event-documents'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { trpc } from '@klear/ui/lib/trpc'
import toNumber from 'lodash/toNumber'
import values from 'lodash/values'
import { AdvanceRecoveryModal } from '@/app/receivables/advance-recovery-modal'
import { NewReceivableButton } from '@/app/receivables/new-receivable-dialog'
import {
  CompletedTable,
  LiveTable,
  AwaitingQuoteTable,
  PendingTable,
  QuotesTable,
  type ReceivableTableContext
} from '@/app/receivables/receivable-tables'
import { useReceivables } from '@/app/receivables/use-receivables'
import { matchesReceivableId, matchesReceivableRow, onlyMatchingTab } from '@/app/receivables/receivable-search'

const TAB_ORDER: readonly ReceivableRowTab[] = [...RECEIVABLE_TABS, 'completed']

const TABS: Record<ReceivableRowTab, { label: string; description: string }> = {
  quotes: {
    label: 'Quotes',
    description: 'New offers on your invoices, earliest invoice first. Accept one to move it to Pending.'
  },
  awaitingQuote: {
    label: 'Awaiting quote',
    description: 'Invoices Klear has not priced yet, earliest first. Each moves to Quotes once Klear prices it.'
  },
  pending: {
    label: 'Pending',
    description:
      'Offers you accepted, earliest invoice first. Klear funds once the purchase order, invoice and proof of acceptance are all approved.'
  },
  live: {
    label: 'Live',
    description:
      'Funded offers, oldest recovery due date first. Repay each advance by that date, or pay now. Extension fees accrue for each day after it.'
  },
  completed: {
    label: 'Completed',
    description: 'Offers that have been repaid and closed, most recently completed first.'
  }
}

type PendingAction = {
  offer: InvoiceOffer
  action: typeof PERMISSION_ACTION_ACCEPT | typeof PERMISSION_ACTION_REJECT
  values: OrderEventActionFormValues
}

function isReceivableTab(value: string | null): value is ReceivableRowTab {
  return value != null && (TAB_ORDER as readonly string[]).includes(value)
}

export default function Receivables() {
  useDocumentTitle('Receivables')
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const parsedTraderId = toNumber(traderId)
  const { user } = useAuth()
  const utils = trpc.useUtils()
  const { getActionConfig } = useEventActionConfig()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab: ReceivableRowTab = isReceivableTab(tabParam) ? tabParam : 'quotes'

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const openAction = (offer: InvoiceOffer, action: PendingAction['action']) =>
    setPendingAction({ offer, action, values: EMPTY_ORDER_EVENT_ACTION_FORM(action) })
  const [actionLoading, setActionLoading] = useState(false)
  const [payNowOffer, setPayNowOffer] = useState<InvoiceOffer | null>(null)

  const canCreateReceivable = !!user?.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_ADD)
  const canAccept = !!user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_ACCEPT)
  const canCancel = !!user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_REJECT)

  const open = useReceivables(parsedTraderId)
  const completed = useReceivables(parsedTraderId, { completed: true })
  const rows = useMemo(() => [...open.rows, ...completed.rows], [open.rows, completed.rows])
  const { counts } = open
  const error = open.error ?? completed.error

  const documentEventIds = useMemo(
    () => rows.flatMap((row) => values(row.documents).flatMap((document) => document.event?.id ?? [])),
    [rows]
  )
  const { documentsFor } = useOrderEventDocuments(documentEventIds, parsedTraderId)

  const context: ReceivableTableContext = {
    traderId: parsedTraderId,
    isLoading: open.isLoading,
    documentsFor,
    canAccept,
    canCancel,
    onAccept: (offer) => openAction(offer, PERMISSION_ACTION_ACCEPT),
    onCancel: (offer) => openAction(offer, PERMISSION_ACTION_REJECT),
    onPayNow: setPayNowOffer,
    orderHref: (orderId, orderEventId) => `${urlPrefixApp}/${ROUTE_ORDERS}/${orderId}?orderEventId=${orderEventId}`
  }

  const setParam = useCallback(
    (key: string, value: string | null) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value === null) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true }
      ),
    [setSearchParams]
  )

  const searchFilter = searchParams.get('search')
  const filteredRows = useMemo(
    () => (searchFilter === null ? rows : rows.filter((row) => matchesReceivableId(row, searchFilter))),
    [rows, searchFilter]
  )
  const firstFilteredTab = searchFilter === null ? undefined : filteredRows[0]?.tab
  useEffect(() => {
    if (tabParam === null && firstFilteredTab !== undefined) setParam('tab', firstFilteredTab)
  }, [tabParam, firstFilteredTab, setParam])

  const [search, setSearch] = useState('')
  const rowsByTab = useMemo(() => {
    const byTab: Record<ReceivableRowTab, ReceivableRow[]> = {
      quotes: [],
      awaitingQuote: [],
      pending: [],
      live: [],
      completed: []
    }
    for (const row of filteredRows) {
      if (matchesReceivableRow(row, search)) byTab[row.tab].push(row)
    }
    return byTab
  }, [filteredRows, search])

  const changeSearch = (value: string) => {
    setSearch(value)
    const matchTab = onlyMatchingTab(filteredRows, value, matchesReceivableRow)
    if (matchTab != null && matchTab !== tab) setParam('tab', matchTab)
  }

  const closeAction = () => setPendingAction(null)

  return (
    <div className="h-full flex-1 flex-col space-y-8 p-8 [contain:inline-size] md:flex">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Receivables</h2>
          <p className="text-muted-foreground">Offers on your invoices, from quote to repayment.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <NewReceivableButton traderId={parsedTraderId} />
          {canCreateReceivable && (
            <ButtonLink Link={Link} to={`${urlPrefixApp}/${ROUTE_RECEIVABLE}`}>
              <Plus className="mr-2 h-4 w-4" />
              New receivable
            </ButtonLink>
          )}
        </div>
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isReceivableTab(value)) setParam('tab', value)
        }}
        className="space-y-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList className="h-10 max-w-full justify-start gap-1 overflow-x-auto">
            {TAB_ORDER.map((id, index) => (
              <Fragment key={id}>
                {index > 0 && (
                  <ArrowRight aria-hidden className="hidden size-4 shrink-0 text-muted-foreground sm:block" />
                )}
                <TabsTrigger value={id} className="h-8 shrink-0 gap-2 px-3.5 text-[15px]">
                  {TABS[id].label}
                  {id !== 'completed' && counts[id] > 0 && (
                    <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-xs tabular-nums text-primary-foreground">
                      <span aria-hidden>{counts[id]}</span>
                      <span className="sr-only">{`${counts[id]} to action`}</span>
                    </span>
                  )}
                </TabsTrigger>
              </Fragment>
            ))}
          </TabsList>
          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
            {searchFilter !== null && (
              <Badge variant="secondary" className="h-9 gap-1.5 pl-3 pr-1.5 text-sm font-medium">
                <ListFilter aria-hidden className="size-3.5 text-muted-foreground" />
                {searchFilter}
                <button
                  type="button"
                  aria-label={`Clear ${searchFilter} filter`}
                  title="Clear filter"
                  onClick={() => setParam('search', null)}
                  className="grid size-6 place-items-center rounded text-muted-foreground hover:bg-background hover:text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              </Badge>
            )}
            <div className="relative w-full sm:w-96">
              <Search className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={search}
                onChange={(event) => changeSearch(event.target.value)}
                placeholder="Search offer, invoice, PO, customer or amount"
                aria-label="Search receivables"
                className="pl-9"
              />
            </div>
          </div>
        </div>
        {error != null && (
          <p role="alert" className="text-sm text-destructive">
            Receivables could not load. Refresh the page, or try again in a few minutes.
          </p>
        )}
        {TAB_ORDER.map((id) => (
          <TabsContent key={id} value={id} className="space-y-4">
            <p className="max-w-prose text-muted-foreground">{TABS[id].description}</p>
            {id === 'quotes' && <QuotesTable rows={rowsByTab.quotes} context={context} />}
            {id === 'awaitingQuote' && <AwaitingQuoteTable rows={rowsByTab.awaitingQuote} context={context} />}
            {id === 'pending' && <PendingTable rows={rowsByTab.pending} context={context} />}
            {id === 'live' && <LiveTable rows={rowsByTab.live} context={context} />}
            {id === 'completed' && (
              <CompletedTable rows={rowsByTab.completed} context={{ ...context, isLoading: completed.isLoading }} />
            )}
          </TabsContent>
        ))}
      </Tabs>
      {pendingAction && (
        <EventActionsConfirmModal
          isOpen
          onClose={closeAction}
          onSuccess={() => {
            if (pendingAction.action === PERMISSION_ACTION_ACCEPT) setParam('tab', 'pending')
            closeAction()
          }}
          eventRef={orderEventRefFromOffer(pendingAction.offer)}
          selectedAction={pendingAction.action}
          actionConfig={getActionConfig(pendingAction.offer.type)[pendingAction.action] ?? null}
          actionValues={pendingAction.values}
          onNotesChange={(notes) =>
            setPendingAction((current) => current && { ...current, values: { ...current.values, notes } })
          }
          isLoading={actionLoading}
          setIsLoading={setActionLoading}
        />
      )}
      {payNowOffer && (
        <AdvanceRecoveryModal
          traderId={parsedTraderId}
          offerId={payNowOffer.id}
          offerIdDisplay={payNowOffer.idDisplay}
          open
          onOpenChange={(open) => {
            if (!open) setPayNowOffer(null)
          }}
          onSuccess={() => {
            void utils.receivable.list.invalidate()
          }}
        />
      )}
    </div>
  )
}
